import ReactGA from 'react-ga4';
import { hasUserConsented } from './consent';

const TRACKING_ID = 'G-EYGMTPEL1F';
const isDevelopment = process.env.NODE_ENV === 'development';
let isInitialized = false;

export const initGA = () => {
  if (!hasUserConsented()) {
    if (isDevelopment) {
      console.log('Google Analytics not initialized - user has not consented');
    }
    return;
  }

  if (isInitialized) {
    if (isDevelopment) {
      console.log('Google Analytics already initialized');
    }
    return;
  }

  try {
    ReactGA.initialize(TRACKING_ID, {
      gaOptions: {
        debug_mode: isDevelopment,
      },
    });

    isInitialized = true;

    if (isDevelopment) {
      console.log('Google Analytics initialized with ID:', TRACKING_ID);
    }
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
  }
};

export const trackPageView = (path, title) => {
  if (!hasUserConsented()) return;

  try {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title
    });

    if (isDevelopment) {
      console.log('GA Page View:', path, title);
    }
  } catch (error) {
    console.error('Failed to track page view:', error);
  }
};

export const trackEvent = (category, action, label, value) => {
  if (!hasUserConsented()) return;

  try {
    const eventParams = {
      category,
      action,
    };

    if (label) eventParams.label = label;
    if (value) eventParams.value = value;

    ReactGA.event(eventParams);

    if (isDevelopment) {
      console.log('GA Event:', eventParams);
    }
  } catch (error) {
    console.error('Failed to track event:', error);
  }
};

export const trackArticleView = (article) => {
  if (!hasUserConsented()) return;

  try {
    ReactGA.event({
      category: 'Article',
      action: 'View',
      label: article.title,
      article_id: article.id,
      article_category: article.category,
      article_author: article.author,
    });

    if (isDevelopment) {
      console.log('GA Article View:', {
        title: article.title,
        id: article.id,
        category: article.category,
        author: article.author,
      });
    }
  } catch (error) {
    console.error('Failed to track article view:', error);
  }
};

export const trackCategoryView = (categoryName) => {
  if (!hasUserConsented()) return;

  try {
    ReactGA.event({
      category: 'Category',
      action: 'View',
      label: categoryName,
    });

    if (isDevelopment) {
      console.log('GA Category View:', categoryName);
    }
  } catch (error) {
    console.error('Failed to track category view:', error);
  }
};

export const trackButtonClick = (buttonName, location) => {
  if (!hasUserConsented()) return;

  try {
    ReactGA.event({
      category: 'Button',
      action: 'Click',
      label: `${buttonName} - ${location}`,
    });

    if (isDevelopment) {
      console.log('GA Button Click:', buttonName, location);
    }
  } catch (error) {
    console.error('Failed to track button click:', error);
  }
};

export const trackFormSubmission = (formName, success = true) => {
  if (!hasUserConsented()) return;

  try {
    ReactGA.event({
      category: 'Form',
      action: success ? 'Submit Success' : 'Submit Failed',
      label: formName,
    });

    if (isDevelopment) {
      console.log('GA Form Submission:', formName, success ? 'Success' : 'Failed');
    }
  } catch (error) {
    console.error('Failed to track form submission:', error);
  }
};

export const trackSocialShare = (platform, articleTitle) => {
  if (!hasUserConsented()) return;

  try {
    ReactGA.event({
      category: 'Social',
      action: 'Share',
      label: `${platform} - ${articleTitle}`,
    });

    if (isDevelopment) {
      console.log('GA Social Share:', platform, articleTitle);
    }
  } catch (error) {
    console.error('Failed to track social share:', error);
  }
};

export const trackArticleCardClick = (articleTitle, location) => {
  if (!hasUserConsented()) return;

  try {
    ReactGA.event({
      category: 'Article Card',
      action: 'Click',
      label: `${articleTitle} - ${location}`,
    });

    if (isDevelopment) {
      console.log('GA Article Card Click:', articleTitle, location);
    }
  } catch (error) {
    console.error('Failed to track article card click:', error);
  }
};

export const trackCategoryCardClick = (categoryName) => {
  if (!hasUserConsented()) return;

  try {
    ReactGA.event({
      category: 'Category Card',
      action: 'Click',
      label: categoryName,
    });

    if (isDevelopment) {
      console.log('GA Category Card Click:', categoryName);
    }
  } catch (error) {
    console.error('Failed to track category card click:', error);
  }
};
